'use client';

import { useRouter, useSearchParams } from 'next/navigation';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { LEAVE_REGISTRATION_MODAL_SHOWN } from '../LeaveRegistrationModal/helper';
import { AnalyticsEventName } from '@/api/analytics';
import { Button, ButtonClose } from '@/components/buttons';
import { HasModalShown } from '@/components/Deposit/helpers';
import { ImageComponent } from '@/components/Image';
import { totalSteps } from '@/helpers/auth';
import { ModalsList } from '@/helpers/breadcrumbModal';
import { removeQueryParameter } from '@/helpers/common';
import { QUERY_VIP } from '@/helpers/modals';
import { useAddBreadcrumbModal } from '@/hooks/addBreadcrumbModal';
import { useAnalytics } from '@/hooks/analytics';
import { useCommonStore } from '@/store';

import { ScLeaveRegistration } from './styled';

export type LeaveRegistrationProps = {
  onClose: () => void;
  onCloseRegistration: () => void;
};

export const LeaveRegistration: FC<LeaveRegistrationProps> = ({
  onClose,
  onCloseRegistration,
}) => {
  const { t } = useTranslation();
  const { sendEventToAnalytics } = useAnalytics();
  const currentRegistrationStep = useCommonStore.use.currentRegistrationStep();

  const searchParams = useSearchParams();
  const { push } = useRouter();

  const [stepsLeft, setStepsLeft] = useState(0);

  useEffect(() => {
    setStepsLeft(
      totalSteps - Number(currentRegistrationStep.split('step').pop()) + 1
    );
  }, [currentRegistrationStep]);

  useAddBreadcrumbModal(ModalsList.LEAVE_REGISTRATION);

  const handleButtonClose = () => {
    onClose();
    sessionStorage.setItem(LEAVE_REGISTRATION_MODAL_SHOWN, HasModalShown.TRUE);

    sendEventToAnalytics({
      name: AnalyticsEventName.MODAL__REG_EXIT_POPUP__CLOSE_CROSS__CLICK,
    });
  };

  const handleNoClick = () => {
    onClose();
    sessionStorage.setItem(LEAVE_REGISTRATION_MODAL_SHOWN, HasModalShown.TRUE);
    sendEventToAnalytics({
      name: AnalyticsEventName.MODAL__REG_EXIT_POPUP_NO__CLICK,
    });
  };

  const handleYesClick = () => {
    if (searchParams.get(QUERY_VIP)) removeQueryParameter(QUERY_VIP, push);
    onCloseRegistration();
    onClose();
    sessionStorage.setItem(LEAVE_REGISTRATION_MODAL_SHOWN, HasModalShown.TRUE);
    sendEventToAnalytics({
      name: AnalyticsEventName.MODAL__REG_EXIT_POPUP__YES__CLICK,
    });
  };

  return (
    <ScLeaveRegistration>
      <ButtonClose dark onClick={handleButtonClose} top={8} right={8} />
      <ImageComponent
        className="emoji-img"
        width={80}
        height={80}
        src="/emojis/thinkingEmoji.webp"
        alt="thinking emoji"
      />
      <h3 className="title">{t('registrationLeaving.title')}</h3>
      <p className="leaving-text">
        {t('registrationLeaving.text', {
          step: `${stepsLeft} ${
            stepsLeft === 1
              ? t('registrationLeaving.step')
              : t('registrationLeaving.steps')
          }`,
        })}
      </p>
      <div className="buttons">
        <Button
          autoPaddingHorizontal
          wide
          small
          blackTransparent
          onClick={handleYesClick}
        >
          {t('yes')}
        </Button>
        <Button
          autoPaddingHorizontal
          wide
          small
          primary
          onClick={handleNoClick}
        >
          {t('no')}
        </Button>
      </div>
    </ScLeaveRegistration>
  );
};
